import type { PartnerConfig } from "@expert/app-config/types";

import { ControlPanelContainer, type ServiceProvider, useIframeControls } from "@expert/service-providers";

import type { CreateSdkParams } from "@/utils/createAgentSdk";

import { GlobalError, WorkspaceLoader } from "@/common-ui";
import KonamiCode from "@/components/KonamiCode";

import type { AgentSdk } from "./agentSdk";

import { AgentSdkContext } from "./AgentSdkContext";
import { type AgentSdkLoaderProps, useAgentSdkLoader } from "./hooks/useAgentSdkLoader";
import { EvlDisconnectedErrorPage } from "./voice/EvlDisconnectedErrorPage";

export type AgentSdkProviderCreator = (params: CreateSdkParams) => Promise<AgentSdk>;

interface AgentSdkProviderProps {
    employeeNum: string | null;
    userFriendlyName?: string;
    children?: React.ReactNode;
    serviceProvider: ServiceProvider;
    partnerConfig?: PartnerConfig;
    onAuthRedirect?: VoidFunction;
    isTrainingModeAllowed?: boolean;
}

type InnerProviderProps = AgentSdkProviderProps & Pick<AgentSdkLoaderProps, "createContainer">;

/** Inner provider so that containers can be rendered before SDK loading. */
function InnerProvider({
    employeeNum,
    children,
    userFriendlyName,
    serviceProvider,
    partnerConfig,
    createContainer,
    onAuthRedirect,
    isTrainingModeAllowed,
}: InnerProviderProps) {
    const { sdk, loadingMessage, errorMessage, failureType } = useAgentSdkLoader({
        employeeNum,
        userFriendlyName,
        serviceProvider,
        createContainer,
        partnerConfig,
        onAuthRedirect,
        isTrainingModeAllowed,
    });

    switch (failureType) {
        case "none":
            break;
        case "evl-failed":
            return <EvlDisconnectedErrorPage />;
        case "general":
            return <GlobalError title="Failed to create Agent SDK" msg={errorMessage} />;
    }

    if (loadingMessage) {
        return <WorkspaceLoader msg={loadingMessage} />;
    }

    return <AgentSdkContext.Provider value={sdk}>{children}</AgentSdkContext.Provider>;
}

const konamiSequencePostfix = Object.freeze(["s", "d", "k"] as const);

export function AgentSdkProvider(props: AgentSdkProviderProps) {
    const { controlPanelResolversByName, showControlPanel, setShowControlPanel, createContainer } = useIframeControls();

    return (
        <>
            {Array.from(controlPanelResolversByName, ([name, resolve]) => (
                <ControlPanelContainer
                    key={name}
                    name={name}
                    opened={showControlPanel}
                    onClose={() => setShowControlPanel(false)}
                    ref={(element) => {
                        if (element) resolve(element);
                    }}
                />
            ))}
            <KonamiCode callback={() => setShowControlPanel(true)} sequencePostfix={konamiSequencePostfix} />
            <InnerProvider {...props} createContainer={createContainer} />
        </>
    );
}
